import React from "react";
import { Globe, RefreshCw, TriangleAlert } from "lucide-react";
import { CampaignItem } from "../../model/Campaign";
import { eur } from "../../helpers/format";

/**
 * Blocked (base-drift) badge. The reason is part of the accessible name — a
 * tooltip alone is unreliable on touch/keyboard.
 */
export function InvalidatedBadge({ item }: { item: CampaignItem }) {
  if (!item.invalidated) return null;
  const reason = item.invalidated_reason || "preço base alterado";
  return (
    <span
      aria-label={`Bloqueado: ${reason}`}
      title={reason}
      data-testid="campaign-item-invalidated"
      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-300"
    >
      <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
      Bloqueado — base alterada
      <span className="sr-only">Motivo: {reason}</span>
    </span>
  );
}

/**
 * Other running/scheduled campaigns the same compound is in (any reseller),
 * with the winning one flagged and an override warning when another campaign
 * is the effective price for this compound.
 */
export function OtherCampaigns({ item }: { item: CampaignItem }) {
  const others = item.other_campaigns ?? [];
  if (others.length === 0) return null;
  const winner = others.find((c) => c.is_effective);
  return (
    <div className="mt-3 text-xs text-gray-600">
      <div className="font-medium text-gray-700">
        Também em {others.length}{" "}
        {others.length === 1 ? "campanha" : "campanhas"} (mesmo composto):
      </div>
      <ul className="mt-1 space-y-0.5">
        {others.map((c) => (
          <li key={c.campaign_id} className="flex items-center gap-1">
            {c.is_global ? (
              <Globe className="h-3 w-3" aria-hidden="true" />
            ) : (
              <span className="text-gray-400">•</span>
            )}
            <span>{c.name}</span>
            <span className="text-gray-400">
              {c.is_global ? "global" : `reseller ${c.reseller_erp_ref ?? "—"}`}
            </span>
            {c.promotional_pvp_with_vat != null && (
              <span className="tabular-nums">
                {eur.format(c.promotional_pvp_with_vat)}
              </span>
            )}
            {c.is_effective && (
              <span className="rounded bg-green-50 px-1.5 py-0.5 font-medium text-green-700">
                efetiva
              </span>
            )}
            {c.invalidated && (
              <span className="rounded bg-red-50 px-1.5 py-0.5 font-medium text-red-700">
                bloqueada
              </span>
            )}
          </li>
        ))}
      </ul>
      {winner && (
        <p className="mt-1 text-amber-700">
          Esta campanha é sobreposta por “{winner.name}” (
          {winner.promotional_pvp_with_vat != null
            ? eur.format(winner.promotional_pvp_with_vat)
            : "preço efetivo"}
          ). O preço desta campanha não será servido.
        </p>
      )}
    </div>
  );
}

/** Re-validate a blocked item (action lives next to Edit/Remove). */
export function RevalidateButton({
  item,
  pending,
  onRevalidate,
}: {
  item: CampaignItem;
  pending?: boolean;
  onRevalidate: (item: CampaignItem) => void;
}) {
  if (!item.invalidated) return null;
  return (
    <button
      type="button"
      disabled={pending}
      onClick={() => onRevalidate(item)}
      aria-label={`Revalidar ${item.compound?.name || item.compound_id}`}
      title="Revalidar com o preço base atual"
      className="min-h-11 min-w-11 inline-flex items-center justify-center text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-lg disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 ${pending ? "animate-spin" : ""}`} />
    </button>
  );
}
